import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { ApiProductStatus, ProductAlias, } from '@ibk/shared-types';
import { baseApi } from '../../api/baseApi';
const createDebounceUtil = () => {
    const timeoutMap = new Map();
    return {
        debounceQuery: async (key, delay, queryFn) => {
            return new Promise((resolve, reject) => {
                const existingTimeout = timeoutMap.get(key);
                if (existingTimeout) {
                    clearTimeout(existingTimeout);
                }
                const timeout = setTimeout(async () => {
                    try {
                        const result = await queryFn();
                        timeoutMap.delete(key);
                        resolve(result);
                    }
                    catch (error) {
                        timeoutMap.delete(key);
                        reject(error instanceof Error ? error : new Error(String(error)));
                    }
                }, delay);
                timeoutMap.set(key, timeout);
            });
        },
        cleanup: () => {
            timeoutMap.forEach(timeout => clearTimeout(timeout));
            timeoutMap.clear();
        },
    };
};
export var Product;
(function (Product) {
    Product["CONTABILIDADE_DIGITAL"] = "contabilidade-digital";
})(Product || (Product = {}));
const normalizeProductStatus = (numericStatus) => {
    switch (numericStatus) {
        case '1':
            return ApiProductStatus.AVAILABLE;
        case '2':
            return ApiProductStatus.ACTIVATING;
        case '3':
            return ApiProductStatus.ACTIVE;
        case '4':
            return ApiProductStatus.DEACTIVATED;
        case '5':
            return ApiProductStatus.NOT_APPROVED;
        default:
            return ApiProductStatus.AVAILABLE;
    }
};
const normalizeProductResponse = (response) => ({
    productAlias: response.productAlias,
    status: normalizeProductStatus(response.status),
    metadata: response.metadata,
});
const debounceUtil = createDebounceUtil();
export const productApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        getProduct: builder.query({
            queryFn: async ({ product, selectedCompanyPartyId, debounceMs = 300 }, api, extraOptions, baseQuery) => {
                const debounceKey = `getProduct-${product}-${selectedCompanyPartyId}`;
                try {
                    const result = await debounceUtil.debounceQuery(debounceKey, debounceMs, async () => {
                        return await baseQuery({
                            url: `/v1/home/products/${product}`,
                            method: 'GET',
                            forceAccountantApiBaseUrl: true,
                            headers: {
                                'x-tr-ibk-selected-company-party-id': selectedCompanyPartyId,
                            },
                        });
                    });
                    if (result.error) {
                        return { error: result.error };
                    }
                    if (result.data) {
                        return { data: result.data };
                    }
                    return { error: { status: 'FETCH_ERROR', error: 'No data received' } };
                }
                catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: String(error) } };
                }
            },
            keepUnusedDataFor: 0,
        }),
        getHomeProduct: builder.query({
            queryFn: async ({ productAlias, selectedCompanyPartyId, selectedCompanyOrgId, debounceMs = 300, accountPjCanSeeBalance }, api, extraOptions, baseQuery) => {
                const debounceKey = `getHomeProduct-${productAlias}-${selectedCompanyPartyId}`;
                if (productAlias === ProductAlias.BENEFICIOS && !selectedCompanyOrgId) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Header x-tr-ibk-selected-company-org-id is required for beneficios product',
                        },
                    };
                }
                try {
                    const result = await debounceUtil.debounceQuery(debounceKey, debounceMs, async () => {
                        const headers = {
                            'x-tr-ibk-selected-company-party-id': selectedCompanyPartyId,
                        };
                        if (selectedCompanyOrgId) {
                            headers['x-tr-ibk-selected-company-org-id'] = selectedCompanyOrgId;
                        }
                        return await baseQuery({
                            url: `/v1/home/products/${productAlias}`,
                            method: 'GET',
                            forceAccountantApiBaseUrl: true,
                            headers,
                        });
                    });
                    if (result.error) {
                        return { error: result.error };
                    }
                    if (result.data) {
                        let responseData = result.data;
                        if (productAlias === ProductAlias.CONTA_DIGITAL && !accountPjCanSeeBalance) {
                            responseData = {
                                ...responseData,
                                result: {
                                    ...responseData.result,
                                    metadata: {},
                                },
                            };
                        }
                        return { data: responseData };
                    }
                    return { error: { status: 'FETCH_ERROR', error: 'No data received' } };
                }
                catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: String(error) } };
                }
            },
            keepUnusedDataFor: 0,
        }),
        getHomeProductV2: builder.query({
            queryFn: async ({ productAlias, selectedCompanyPartyId, selectedCompanyOrgId, debounceMs = 300, accountPjCanSeeBalance }, api, extraOptions, baseQuery) => {
                const debounceKey = `getHomeProductV2-${productAlias}-${selectedCompanyPartyId}`;
                if (productAlias === ProductAlias.BENEFICIOS && !selectedCompanyOrgId) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Header x-tr-ibk-selected-company-org-id is required for beneficios product',
                        },
                    };
                }
                try {
                    const result = await debounceUtil.debounceQuery(debounceKey, debounceMs, async () => {
                        const partyId = getSelectedAccountInfo()?.targetId;
                        const headers = {
                            'x-tr-ibk-selected-company-party-id': selectedCompanyPartyId,
                        };
                        if (selectedCompanyOrgId) {
                            headers['x-tr-ibk-selected-company-org-id'] = selectedCompanyOrgId;
                        }
                        if (partyId) {
                            headers['x-tr-party-selected'] = partyId;
                        }
                        return await baseQuery({
                            url: `/v2/home/products/${productAlias}`,
                            method: 'GET',
                            forceAccountantApiBaseUrl: true,
                            headers,
                        });
                    });
                    if (result.error) {
                        return { error: result.error };
                    }
                    if (result.data) {
                        let responseData = result.data;
                        if (productAlias === ProductAlias.CONTA_DIGITAL && !accountPjCanSeeBalance) {
                            responseData = {
                                ...responseData,
                                result: {
                                    ...responseData.result,
                                    metadata: {},
                                },
                            };
                        }
                        return { data: responseData };
                    }
                    return { error: { status: 'FETCH_ERROR', error: 'No data received' } };
                }
                catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: String(error) } };
                }
            },
            keepUnusedDataFor: 0,
        }),
        getProductRelationShips: builder.query({
            query: ({ targetValue }) => ({
                url: `/v1/products/management/product-relationships`,
                params: { targetType: 'PARTY', targetValue },
                headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
            }),
            transformResponse: (response) => {
                return response.result;
            },
        }),
    }),
    overrideExisting: false,
});
export const cleanupProductApiDebounce = debounceUtil.cleanup;
export { normalizeProductResponse, normalizeProductStatus };
export const { useGetProductQuery, useGetHomeProductQuery, useGetHomeProductV2Query, useLazyGetProductQuery, useLazyGetHomeProductQuery, useLazyGetHomeProductV2Query, useLazyGetProductRelationShipsQuery, } = productApi;
